// Medication schedules (SPEC.md §4.3 medications, med_doses). Pure functions; dates are local
// 'yyyy-MM-dd', times 'HH:mm'.
import { addDays, format, parse } from 'date-fns';
import { parseDays, WEEKDAYS, type Weekday } from './schedule';

export type DayRule = 'every_day' | 'dialysis_days' | 'non_dialysis_days' | 'specific_days';

export interface MedicationRow {
  id: string;
  name: string;
  strength: string | null;
  form: 'tablet' | 'capsule' | 'injection' | 'sachet' | 'drops' | 'other' | null;
  dose: string | null;
  category: 'phosphate_binder' | 'esa' | 'iron' | 'vitamin_d' | 'bp' | 'diabetes' | 'other' | null;
  /** JSON array of 'HH:mm'. */
  times: string;
  with_meals: number;
  day_rule: DayRule;
  specific_days: string | null;
  given_at_center: number;
  start_date: string | null;
  end_date: string | null;
  prescribed_by: string | null;
  notes: string | null;
  active: number;
}

export interface DoseRow {
  id: string;
  medication_id: string;
  /** Local 'yyyy-MM-ddTHH:mm'. */
  scheduled_for: string;
  status: 'taken' | 'skipped';
  logged_at: string;
}

export interface PlannedDose {
  med: MedicationRow;
  time: string;
  scheduledFor: string;
  /** Stable id, so the same dose logged on two devices is one row. */
  doseId: string;
}

export function medTimes(med: Pick<MedicationRow, 'times'>): string[] {
  try {
    const parsed = JSON.parse(med.times) as unknown;
    return Array.isArray(parsed) ? parsed.filter((time): time is string => typeof time === 'string' && /^\d{2}:\d{2}$/.test(time)).sort() : [];
  } catch {
    return [];
  }
}

export const doseId = (medicationId: string, scheduledFor: string) => `${medicationId}:${scheduledFor}`;

const weekdayOf = (date: string): Weekday => WEEKDAYS[(parse(date, 'yyyy-MM-dd', new Date()).getDay() + 6) % 7];

/** Whether `med` is taken at home on `date`. Medicines given at the dialysis centre aren't. */
export function takesOn(med: MedicationRow, date: string, isDialysisDay: boolean) {
  if (!med.active || med.given_at_center) return false;
  if (med.start_date && date < med.start_date) return false;
  if (med.end_date && date > med.end_date) return false;
  switch (med.day_rule) {
    case 'every_day':
      return true;
    case 'dialysis_days':
      return isDialysisDay;
    case 'non_dialysis_days':
      return !isDialysisDay;
    case 'specific_days':
      return parseDays(med.specific_days).includes(weekdayOf(date));
  }
}

/** Doses due on `date`, by time. */
export function dosesOn(meds: MedicationRow[], date: string, isDialysisDay: boolean): PlannedDose[] {
  return meds
    .filter((med) => takesOn(med, date, isDialysisDay))
    .flatMap((med) =>
      medTimes(med).map((time) => {
        const scheduledFor = `${date}T${time}`;
        return { med, time, scheduledFor, doseId: doseId(med.id, scheduledFor) };
      })
    )
    .sort((a, b) => a.time.localeCompare(b.time) || a.med.name.localeCompare(b.med.name));
}

export interface Adherence {
  planned: number;
  taken: number;
  skipped: number;
}

/** Planned doses from `from` to `to` (inclusive) against the logged ones, per medicine. */
export function adherence(meds: MedicationRow[], doses: DoseRow[], from: string, to: string, dialysisDates: Set<string>) {
  const logged = new Map(doses.map((dose) => [dose.id, dose.status]));
  const byMed = new Map<string, Adherence>();
  for (let day = parse(from, 'yyyy-MM-dd', new Date()); format(day, 'yyyy-MM-dd') <= to; day = addDays(day, 1)) {
    const date = format(day, 'yyyy-MM-dd');
    for (const dose of dosesOn(meds, date, dialysisDates.has(date))) {
      const entry = byMed.get(dose.med.id) ?? { planned: 0, taken: 0, skipped: 0 };
      entry.planned++;
      const status = logged.get(dose.doseId);
      if (status === 'taken') entry.taken++;
      else if (status === 'skipped') entry.skipped++;
      byMed.set(dose.med.id, entry);
    }
  }
  return byMed;
}

export const FORM_LABELS: Record<NonNullable<MedicationRow['form']>, string> = {
  tablet: 'Tablet',
  capsule: 'Capsule',
  injection: 'Injection',
  sachet: 'Sachet',
  drops: 'Drops',
  other: 'Other'
};

export const CATEGORY_LABELS: Record<NonNullable<MedicationRow['category']>, string> = {
  phosphate_binder: 'Phosphate binder',
  esa: 'Anaemia injection (ESA)',
  iron: 'Iron',
  vitamin_d: 'Vitamin D',
  bp: 'Blood pressure',
  diabetes: 'Diabetes',
  other: 'Other'
};

export const DAY_RULE_LABELS: Record<DayRule, string> = {
  every_day: 'Every day',
  dialysis_days: 'Dialysis days',
  non_dialysis_days: 'Days without dialysis',
  specific_days: 'Certain days'
};
